import { useState, useEffect } from "react";
import { Toaster } from "sonner";
import { Routes, Route } from "react-router-dom";

import { useAuth } from "./hooks/useAuth";
import { useSubdomain } from "./hooks/useSubdomain";
import LoginPage from "./views/LoginPage";
import LandingPage from "./views/LandingPage";
import AppRouter from "./AppRouter";
import SetupWizard from "./views/setup/SetupWizard";
import SchoolWebsite from './views/schoolwebsite/SchoolWebsite';
import { subscribeStudentOnLogin } from "./utils/pushNotifications";
import IOSInstallBanner from "./components/IOSInstallBanner";

/* Minimal branded loader — shown while auth token is being verified
   or while the school slug is being resolved on a subdomain. */
function AppLoader() {
  return (
    <div
      style={{
        position: "fixed", inset: 0, display: "flex", alignItems: "center",
        justifyContent: "center", flexDirection: "column", gap: 20,
        background: "linear-gradient(160deg, #0A0F1E 0%, #0D1B36 50%, #0E1628 100%)",
      }}
    >
      <div style={{ width: 72, height: 72, borderRadius: 18, overflow: "hidden", boxShadow: "0 12px 40px rgba(79,70,229,0.4)" }}>
        <img src="/logo192.png" alt="EduPulseAI" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </div>
      <div style={{ width: 160, height: 2, borderRadius: 4, background: "rgba(255,255,255,0.08)", overflow: "hidden", position: "relative" }}>
        <div style={{
          position: "absolute", left: 0, top: 0, height: "100%", width: "40%", borderRadius: 4,
          background: "linear-gradient(90deg, #4F46E5, #E8690A)",
          animation: "slideBar 1.2s ease-in-out infinite",
        }} />
      </div>
      <style>{`@keyframes slideBar{0%{left:-40%}100%{left:100%}}`}</style>
    </div>
  );
}

export default function AppIndex() {
  const [lang, setLang] = useState("en");
  const { user, logout, sendOTP, verifyOTP, ssoLogin, requiresSetup, authReady } = useAuth();
  const subdomain = useSubdomain();

  useEffect(() => {
    if (user && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      const timer = setTimeout(() => {
        subscribeStudentOnLogin(user);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  useEffect(() => {
    const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
    if (user && isAdmin && requiresSetup && window.location.pathname !== '/setup-wizard') {
      window.location.href = '/setup-wizard';
    }
  }, [user, requiresSetup]);

  // Wait until auth token verification AND subdomain school lookup both complete
  // before deciding what to render — prevents any flash between states.
  const subdomainPending = subdomain.isSubdomain && subdomain.loading;
  if (!authReady || subdomainPending) {
    return <AppLoader />;
  }

  const isSetupWizard = window.location.pathname === '/setup-wizard';

  const loginPage = (
    <LoginPage
      onSendOTP={sendOTP}
      onVerifyOTP={verifyOTP}
      onSSOLogin={ssoLogin}
      lang={lang}
      setLang={setLang}
      subdomain={subdomain}
    />
  );

  const appRouter = isSetupWizard ? <SetupWizard /> : <AppRouter onLogout={logout} />;

  // On a school subdomain (e.g. dev.edupulseai.co.in), unauthenticated users
  // should land directly on the school's login page — not the marketing landing page.
  const unauthDefault = subdomain.isSubdomain ? loginPage : <LandingPage />;

  return (
    <>
      <Toaster position="top-right" richColors closeButton />
      <IOSInstallBanner />
      <Routes>
        <Route path="/s/:slug" element={<SchoolWebsite />} />
        <Route path="/"      element={user ? appRouter : unauthDefault} />
        <Route path="/login" element={user ? appRouter : loginPage} />
        <Route path="*"      element={user ? appRouter : unauthDefault} />
      </Routes>
    </>
  );
}
